import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Image, X, Bold, Italic, Underline, List, ListOrdered, Download, Edit2, ChevronRight, ChevronLeft } from 'lucide-react';
import { useDocumentation } from '../context/DocumentationContext';
import styles from './FileEditor.module.css';

const FileEditor = () => {
  const { cardId, '*': filePath } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cards, updateCardFolders } = useDocumentation();
  
  const card = cards[cardId];
  const isNewFile = !filePath;
  const initialPath = searchParams.get('path');
  
  const [fileName, setFileName] = useState('');
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [fileData, setFileData] = useState(null);
  const editorRef = useRef(null);
  const inlinePhotoInputRef = useRef(null);
  const selectedImageRef = useRef(null);
  const [selectedImageWidth, setSelectedImageWidth] = useState('');
  const modeParam = searchParams.get('mode');

  const isViewMode = fileData?.type === 'uploaded-file';
  const mode = modeParam || (isNewFile ? 'edit' : 'view');
  const isReadOnly = mode === 'view' || isViewMode;

  useEffect(() => {
    // Allow browser-native object resizing where supported
    try {
      document.execCommand('enableObjectResizing', false, 'true');
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!isNewFile && card && filePath) {
      // Navigate to the file and get its data
      const pathParts = filePath.split('/');
      let current = card.folders.root;
      
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current.children[pathParts[i]];
      }
      
      const file = current?.children[pathParts[pathParts.length - 1]];
      if (file) {
        setFileData(file);
        setFileName(pathParts[pathParts.length - 1]);
        setContent(file.content || '');
        setPhotos(file.photos || []);
      }
    }
  }, [filePath, card, isNewFile]);

  useEffect(() => {
    if (!editorRef.current) return;
    if (fileData && !isNewFile) {
      editorRef.current.innerHTML = fileData.content || '';
    } else if (isNewFile) {
      editorRef.current.innerHTML = '';
    }
  }, [fileData, isNewFile]);

  useEffect(() => {
    if (!editorRef.current) return;
    if (!isReadOnly) {
      editorRef.current.innerHTML = content || '';
    }
    // Do not update while typing to avoid cursor jumps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly]);

  useEffect(() => {
    if (fileData) {
      setContent(fileData.content || '');
      setPhotos(fileData.photos || []);
    }
  }, [fileData]);

  if (!card) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Card not found</h2>
        <button onClick={() => navigate('/documentation')}>Back to Documentation</button>
      </div>
    );
  }

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const photoData = files.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file)
    }));
    setPhotos([...photos, ...photoData]);
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const applyFormat = (command) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command);
    }
  };

  const increaseIndent = () => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('indent');
    }
  };

  const decreaseIndent = () => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('outdent');
    }
  };

  const handleInlinePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !editorRef.current) return;

    for (const file of files) {
      await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          editorRef.current.focus();
          document.execCommand('insertImage', false, ev.target.result);
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }

    // Update stored content and photo list to keep previews consistent
    setContent(editorRef.current.innerHTML);
    setPhotos((prev) => [
      ...prev,
      ...files.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file)
      }))
    ]);

    // Reset input so the same file can be selected again if needed
    e.target.value = '';
  };

  const handleEditorClick = (e) => {
    if (!editorRef.current) return;
    if (e.target.tagName === 'IMG') {
      selectedImageRef.current = e.target;
      const widthVal = e.target.style.width || `${e.target.naturalWidth}px`;
      const parsed = parseInt(widthVal, 10);
      setSelectedImageWidth(Number.isNaN(parsed) ? '' : parsed.toString());
    } else {
      selectedImageRef.current = null;
      setSelectedImageWidth('');
    }
  };

  const updateSelectedImageWidth = (value) => {
    setSelectedImageWidth(value);
    const img = selectedImageRef.current;
    if (img) {
      if (value) {
        img.style.width = `${value}px`;
        img.style.height = 'auto';
      } else {
        img.style.width = '';
        img.style.height = '';
      }
      setContent(editorRef.current?.innerHTML || '');
    }
  };

  const saveFile = () => {
    if (!fileName.trim()) {
      alert('Please enter a file name');
      return;
    }

    setIsSaving(true);

    const newFolders = JSON.parse(JSON.stringify(card.folders));
    let current = newFolders.root;
    
    // Navigate to the correct folder
    const pathParts = isNewFile && initialPath ? initialPath.split('/') : (filePath ? filePath.split('/').slice(0, -1) : []);
    for (let pathPart of pathParts) {
      current = current.children[pathPart];
    }

    // Save the file
    current.children[fileName] = {
      type: 'file',
      content,
      photos,
      created: fileData?.created || new Date().toLocaleString(),
      modified: new Date().toLocaleString()
    };

    updateCardFolders(cardId, newFolders);
    setIsSaving(false);
  };

  const downloadCurrentFile = () => {
    if (isNewFile) return;
    if (isViewMode && fileData?.url) {
      const link = document.createElement('a');
      link.href = fileData.url;
      link.download = fileName;
      link.click();
      return;
    }
    const blob = new Blob([content || ''], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate(`/documentation/${cardId}`)}
            className={styles.backBtn}
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <h1 style={{ margin: 0, color: '#1e293b', fontSize: '24px' }}>
            {isNewFile ? 'New Document' : fileName}
          </h1>
        </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {!isNewFile && (
              <button onClick={downloadCurrentFile} className={styles.saveBtn}>
                <Download size={18} />
                Download
              </button>
            )}
            {!isReadOnly && (
              <button onClick={saveFile} className={styles.saveBtn} disabled={isSaving}>
                <Save size={18} />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            )}
            {!isNewFile && isReadOnly && !isViewMode && (
              <button
                onClick={() => navigate(`/documentation/${cardId}/file/${filePath}?mode=edit`)}
                className={styles.saveBtn}
              >
                <Edit2 size={18} />
                Edit
              </button>
            )}
          </div>
      </div>

        {isViewMode ? (
        <div className={styles.viewerContainer}>
          <div className={styles.fileInfo}>
            <p><strong>File Type:</strong> {fileData.fileType || 'Unknown'}</p>
            <p><strong>Size:</strong> {(fileData.size / 1024).toFixed(2)} KB</p>
            <p><strong>Uploaded:</strong> {fileData.created}</p>
          </div>

          <div className={styles.preview}>
            {fileData.fileType?.startsWith('image/') ? (
              <img src={fileData.url} alt={fileName} style={{ width: '100%', borderRadius: '8px' }} />
            ) : fileData.fileType === 'application/pdf' ? (
              <iframe src={fileData.url} style={{ width: '100%', height: '600px', border: 'none', borderRadius: '8px' }} title={fileName} />
            ) : (
              <div style={{ textAlign: 'center', padding: '60px' }}>
                <Download size={64} color="#94a3b8" style={{ marginBottom: '20px' }} />
                <p style={{ color: '#64748b', marginBottom: '20px' }}>Preview not available</p>
                <a href={fileData.url} download={fileName} className={styles.downloadBtn}>
                  <Download size={16} />
                  Download File
                </a>
              </div>
            )}
          </div>
        </div>
      ) : isReadOnly ? (
        <div className={styles.viewerContainer}>
          <div className={styles.fileInfo}>
            <p><strong>Created:</strong> {fileData?.created}</p>
            <p><strong>Last Modified:</strong> {fileData?.modified}</p>
          </div>
          <div className={styles.preview}>
            <div
              className={styles.editor}
              style={{ borderRadius: '8px', borderColor: '#e2e8f0' }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
          {photos.length > 0 && (
            <div className={styles.photoSection}>
              <label>Attached Photos</label>
              <div className={styles.photoGrid}>
                {photos.map((photo, idx) => (
                  <div key={idx} className={styles.photoItem}>
                    <img src={photo.url} alt={`preview-${idx}`} />
                    <p>{photo.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.editorContainer}>
          <div className={styles.fileNameInput}>
            <label>File Name</label>
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="Enter file name (e.g., Installation Guide)"
              disabled={!isNewFile}
            />
          </div>

          <div className={styles.editorSection}>
            <label>Content</label>
            <div className={styles.toolbar}>
              <button type="button" onClick={() => applyFormat('bold')} title="Bold">
                <Bold size={16} />
              </button>
              <button type="button" onClick={() => applyFormat('italic')} title="Italic">
                <Italic size={16} />
              </button>
              <button type="button" onClick={() => applyFormat('underline')} title="Underline">
                <Underline size={16} />
              </button>
              <button type="button" onClick={() => applyFormat('insertUnorderedList')} title="Bullet List">
                <List size={16} />
              </button>
              <button type="button" onClick={() => applyFormat('insertOrderedList')} title="Numbered List">
                <ListOrdered size={16} />
              </button>
              <button type="button" onClick={increaseIndent} title="Increase Indent">
                <ChevronRight size={16} />
              </button>
              <button type="button" onClick={decreaseIndent} title="Decrease Indent">
                <ChevronLeft size={16} />
              </button>
              <>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  ref={inlinePhotoInputRef}
                  style={{ display: 'none' }}
                  onChange={handleInlinePhotoSelect}
                />
                <button type="button" onClick={() => inlinePhotoInputRef.current?.click()} title="Insert Photo">
                  <Image size={16} />
                </button>
              </>
            </div>
            <div
              contentEditable
              ref={editorRef}
              onInput={(e) => setContent(e.currentTarget.innerHTML)}
              onClick={handleEditorClick}
              className={styles.editor}
              placeholder="Start typing your documentation..."
              suppressContentEditableWarning
            />
            {!isReadOnly && selectedImageRef.current && (
              <div className={styles.imageSizeRow}>
                <label>Image width (px)</label>
                <div className={styles.imageSizeControls}>
                  <input
                    type="number"
                    min="50"
                    max="2000"
                    value={selectedImageWidth}
                    onChange={(e) => updateSelectedImageWidth(e.target.value)}
                  />
                  <button type="button" onClick={() => updateSelectedImageWidth('')}>
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className={styles.photoSection}>
            <label>Attached Photos</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
              id="photoUpload"
            />
            <label htmlFor="photoUpload" className={styles.uploadBtn}>
              <Image size={20} />
              Upload Photos
            </label>

            {photos.length > 0 && (
              <div className={styles.photoGrid}>
                {photos.map((photo, idx) => (
                  <div key={idx} className={styles.photoItem}>
                    <img src={photo.url} alt={`preview-${idx}`} />
                    <button onClick={() => removePhoto(idx)} className={styles.removeBtn}>
                      <X size={14} />
                    </button>
                    <p>{photo.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileEditor;
